import React, {useState, useEffect} from 'react'
import Plot from 'react-plotly.js'
import {Select} from 'antd'
import axios from 'axios'
import { useSelectedWeek } from '../../../../../../contexts/SelectedWeekContext'

interface WeekData {
  week: string
  positivity: number
  tested: number
}

const StreamgraphChart: React.FC = () => {
  const {selectedWeek} = useSelectedWeek('2023-W40')
  //const [data, setData] = useState(generateRandomData(20))
  const [data, setData] = useState<WeekData[]>([])
  const API_URL = process.env.REACT_APP_API_URL
  const [missingData, setMissingData] = useState<WeekData[]>([])
 
  function addMissingWeek(data: WeekData[]): WeekData[] {
    const startWeek = '2023-W34' // First week in the data
    const endWeek = '2024-W18' // Last week in the data
    const existingWeeks = new Set(data.map((item) => item.week)) // Set to store existing weeks
    const missingData: WeekData[] = []
    const newData: WeekData[] = []

    let currentWeek = startWeek

    while (currentWeek !== endWeek) {
      // Move to the next week
      const [year, weekNum] = currentWeek.split('-W')
      const weekNumber = parseInt(weekNum)
      if (weekNumber < 52) {
        currentWeek = `${year}-W${(weekNumber + 1).toString()}`
      } else {
        currentWeek = `${parseInt(year) + 1}-W1`
      }
      if (!existingWeeks.has(currentWeek)) {
        newData.push({
          week: currentWeek,
          tested: 0,
          positivity: 0,
        })
        missingData.push({
          week: currentWeek,
          tested: 0,
          positivity: 0,
        })
      }
    }

    // Find the index to insert the new week
    let insertionIndex = 0
    for (let i = 0; i < data.length; i++) {
      if (data[i].week <= '2023-W52') {
        insertionIndex = i + 1
      } else {
        break
      }
    }
    setMissingData(missingData)
    // Merge existing data with the newly added missing weeks
    const mergedData = [
      ...data.slice(0, insertionIndex),
      ...newData,
      ...data.slice(insertionIndex),
    ].sort((a, b) => {
      const aYear = parseInt(a.week.substring(0, 4), 10)
      const aWeek = parseInt(a.week.substring(a.week.indexOf('-W') + 2), 10)
      const bYear = parseInt(b.week.substring(0, 4), 10)
      const bWeek = parseInt(b.week.substring(b.week.indexOf('-W') + 2), 10)
      if (aYear !== bYear) {
        return aYear - bYear
      } else {
        return aWeek - bWeek
      }
    })

    return mergedData
  }
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API_URL}/respiratory_virus_positivity_rates`)
        setData(addMissingWeek(response.data))
      } catch (error) {
        console.error('Error fetching data:', error)
        // Handle error or set a default value for data
        setData([])
      }
    }

    fetchData()
  }, [selectedWeek])
  // Sample data
  const selectedYear = parseInt(selectedWeek.slice(0, 4));
const selectedWeekNumber = parseInt(selectedWeek.slice(6));
const startDate = 2023;
const startWeek = 36;
const endYear = 2024;
const endWeek = 18;
const showArrow = (selectedYear > startDate || (selectedYear === startDate && selectedWeekNumber >= startWeek)) &&
                  (selectedYear < endYear || (selectedYear === endYear && selectedWeekNumber <= endWeek));

  const layout = {
    title: 'Respiratory viruses - Positivity rate among sentinel samples tested',
    barmode: 'stack',
    bargap: 0.2, // Adjust the gap between bars
    bargroupgap: 0.1, // Adjust the gap between bar groups
    autosize: true,
    annotations: [
      {
        xref: 'paper',
        yref: 'paper',
        x: -0.035,
        xanchor: 'right',
        y: 0.5,
        yanchor: 'bottom',
        text: '#',
        font: {
          size: 25,
          color: '#008CFF',
        },
        showarrow: false,
      },
      {
        xref: 'paper',
        yref: 'paper',
        x: 1.03,
        xanchor: 'left',
        y: 0.5,
        yanchor: 'bottom',
        text: '%',
        font: {
          size: 25,
          color: '#203879',
        },
        showarrow: false,
      },
      {
        x: selectedWeek,
        y: 0,
        xref: 'x',
        yref: 'y',
        text: showArrow ? 'Selected Week' : '',
        font: {
          family: 'Courier New, monospace',
          size: 16,
          color: '#CC1B1B',
        },
        arrowcolor: '#CC1B1B',
        showarrow: showArrow,
        arrowhead: 7,
        ax: 0,
        ay: -200,
      },
    ],

    xaxis: {
      title: 'Weeks',
      autorange: true,
      type: 'category',
      tickangle: -45,
    },
    yaxis: {
      autorange: true,
    },
    yaxis2: {
      showgrid: false,
      titlefont: {color: '#203879'},
      tickfont: {color: '#203879'},
      overlaying: 'y',
      side: 'right',
      autorange: true,
      zeroline: false,
    },
    legend: {
      x: 0,
      y: -0.4, // Adjust the value to position the legend as per your requirement
      orientation: 'h', // 'h' for horizontal, 'v' for vertical
    },
  }
  return (
    <>
      <Plot
        style={{width: '100%', height: '400px'}}
        data={[
          {
            type: 'scatter',
            mode: 'lines',

            x: data.map((entry) => entry.week),
            y: data.map((entry) => entry.positivity),
            marker: {color: '#203879'},
            yaxis: 'y2',
            name: 'Positivity rate',
          },
          {
            x: missingData.map((entry) => entry.week),
            y: missingData.map((entry) => entry.positivity),
            type: 'scatter',
            mode: 'markers',
            yaxis: 'y2',
            name: 'Missing Data',
            marker: {color: '#000000'},
          },
          {
            type: 'bar',

            x: data.map((entry) => entry.week),
            y: data.map((entry) => entry.tested),
            marker: {color: '#008CFF'},
            name: 'Number of sentinel samples tested',
          },
        ]}
        layout={layout}
        config={{
          displaylogo: false,
          responsive: true,
        }}
      />
    </>
  )
}

export default StreamgraphChart
